import { Button, Card, Divider, Flex, Form, Input } from "antd";
import { useForm } from "antd/es/form/Form";
import CardSectionTitle from "components/CardSectionTitle.component";
import { useAuth } from "context/AuthContextProvider";
import { useChangePassword, useChangePhone } from "hooks/mutations/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { User } from "shared/types";
import PhoneInput, { phoneRules } from "shared/components/PhoneInput";
import { showConfirmModal } from "components/modals/confirmModal";

interface UserProfileCardProps {
  user: User;
}

const SecurityProfileCard = ({ user }: UserProfileCardProps) => {
  const navigate = useNavigate();
  const [passwordForm] = useForm();
  const [phoneNumberForm] = useForm();
  const { signOut } = useAuth();
  const { mutate: changePassword } = useChangePassword();
  const { mutate: changePhone } = useChangePhone();
  const [isDisabled, setIsDisabled] = useState(true);

  // Both submits confirm first: the phone change signs the admin out on the
  // spot (the account goes back to unverified and the next login runs OTP on
  // the new number), and a password change is easy to fire by accident from
  // a form whose fields still hold the previous attempt.
  const onChangePassword = (values: any) => {
    showConfirmModal({
      tone: "amber",
      title: translate("messages.warning.change_password.title"),
      content: translate("messages.warning.change_password.content"),
      confirmLabel: translate("button.save_password"),
      onConfirm: () =>
        changePassword(
          {
            user_id: user.id,
            password: values.password,
            new_password: values.new_password,
          },
          // Clear the three fields only once the server accepted the change;
          // on a wrong old password the admin keeps what they typed.
          { onSuccess: () => passwordForm.resetFields() }
        ),
    });
  };

  // Ensures that the user is unable to submit if the email is still the same
  const onChangePhone = (values: any) => {
    setIsDisabled(user.phone_number === values[0].value);
  };

  const onChangePhoneSubmit = (values: any) => {
    showConfirmModal({
      tone: "amber",
      title: translate("messages.warning.change_phone.title"),
      content: translate("messages.warning.change_phone.content"),
      confirmLabel: translate("button.change_phone"),
      onConfirm: () =>
        changePhone({ user_id: user.id, phone_number: values.phone_number }),
    });
  };

  return (
    <Card>
      <Flex vertical gap={20}>
        <CardSectionTitle
          title={translate("title.security")}
          subtitle={translate("subtitle.security")}
        />
        <Form
          form={phoneNumberForm}
          name="phone-form"
          labelAlign="left"
          labelCol={{ span: 6 }}
          colon={false}
          requiredMark={false}
          initialValues={{
            phone_number: user.phone_number,
          }}
          onFieldsChange={onChangePhone}
          onFinish={onChangePhoneSubmit}
        >
          <Form.Item
            name="phone_number"
            label={translate("label.phone_number")}
            rules={phoneRules({ smsOtp: true })}
          >
            <PhoneInput smsOtp />
          </Form.Item>

          <Form.Item>
            {/* Button system: navy confirm. */}
              <Button type="primary" htmlType="submit" disabled={isDisabled}>
                {translate("button.change_phone")}
              </Button>
          </Form.Item>
        </Form>
        <Divider />
        <Form
          form={passwordForm}
          name="password-form"
          labelAlign="left"
          labelCol={{ span: 6 }}
          colon={false}
          requiredMark={false}
          onFinish={onChangePassword}
        >
          <Form.Item
            name="password"
            label={translate("label.old_password")}
            rules={[
              { required: true, message: translate("form.error.old_password") },
            ]}
            hasFeedback
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="new_password"
            label={translate("label.new_password")}
            rules={[
              {
                required: true,
                message: `${translate("form.error.input")} ${translate(
                  "label.new_password"
                )}`,
              },
              { min: 6, message: translate("form.error.password") },
            ]}
            hasFeedback
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label={translate("label.confirm_password")}
            dependencies={["new_password"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: `${translate("form.error.input")} ${translate(
                  "label.confirm_password"
                )}`,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(translate("form.error.two_password"))
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item>
            {/* Button system: navy confirm. */}
              <Button type="primary" htmlType="submit">
                {translate("button.save_password")}
              </Button>
          </Form.Item>
        </Form>
        <Divider />
        <Button
          color="danger"
          variant="outlined"
          style={{ width: 150, marginBottom: 30, alignSelf: "flex-end" }}
          onClick={() => {
            signOut();
            navigate(PATHS.LOGIN, { replace: true });
          }}
        >
          {translate("button.logout")}
        </Button>
      </Flex>
    </Card>
  );
};

export default SecurityProfileCard;
